import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useDispatch } from 'react-redux';
import { useLocation } from "react-router";
import { fetchEnd, fetchStart, useNotify } from "react-admin";
import { apiUrl } from "../../dataProvider";

const buildDatewiseTrend = (metrics, mapper) => {
    if (!metrics?.datewise) return undefined;
    return Object.keys(metrics.datewise).map(date => mapper(date, metrics.datewise[date]));
};

function GetSiteMetrics() {
    const [metrics, setMetrics] = useState(null);
    const [loading, setLoading] = useState(false);
    const dispatch = useDispatch();
    const notify = useNotify();
    const { search } = useLocation();

    useEffect(() => {
        let isMounted = true;

        const fetchSiteMetrics = async (dates) => {
            setLoading(true);
            dispatch(fetchStart());

            try {
                const { data } = await axios.get(`${apiUrl}/website/metrics${dates}`);
                if (isMounted) {
                    setMetrics(data);
                }
            } catch (error) {
                const response = error?.response;
                const message = response?.statusText;
                const status = response?.status;
                if (status === 401 || status === 403) {
                    window.location.replace('/#/login');
                }
                if (message) {
                    notify(message, 'warning');
                }
            } finally {
                if (isMounted) {
                    setLoading(false);
                }
                dispatch(fetchEnd());
            }
        };

        fetchSiteMetrics(search || '');

        return () => {
            isMounted = false;
        };
    }, [dispatch, notify, search]);

    const storeLoginTrend = useMemo(
        () => buildDatewiseTrend(metrics, (date, entry) => ({
            date,
            logins: entry.users,
        })),
        [metrics],
    );

    const revenuesTrend = useMemo(
        () => buildDatewiseTrend(metrics, (date, entry) => ({
            date,
            revenue_b2b: Number(entry.revenue_b2b),
            revenue_b2c: Number(entry.revenue_b2c),
        })),
        [metrics],
    );

    const valuesTrend = useMemo(
        () => buildDatewiseTrend(metrics, (date, entry) => ({
            date,
            avg_value_b2b: Number(entry.avg_value_b2b),
            avg_value_b2c: Number(entry.avg_value_b2c),
        })),
        [metrics],
    );

    const usersTrend = useMemo(
        () => buildDatewiseTrend(metrics, (date, entry) => ({
            date,
            users: entry.new_store_users,
        })),
        [metrics],
    );

    const ordersTrend = useMemo(
        () => buildDatewiseTrend(metrics, (date, entry) => ({
            date,
            direct_orders: Number(entry.direct_orders),
            store_orders: Number(entry.store_orders),
        })),
        [metrics],
    );

    return {
        metrics,
        storeLoginTrend,
        valuesTrend,
        revenuesTrend,
        usersTrend,
        ordersTrend,
        loading,
    };
}

export default GetSiteMetrics;
